import * as _ from "lodash";
import { IAction } from "../model/IAction";
import { ElementType, IElement, IElementDictionary } from "../model/IProcessData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";

import * as AddArrayElementActions from "../actions/AddArrayElement";
import * as AddOrUpdateElementActions from "../actions/AddOrUpdateElement";
import * as ImportExportActions from "../actions/ImportExportActions";
import * as RemoveArrayElementActions from "../actions/RemoveArrayElement";
import * as RemoveElementActions from "../actions/RemoveElement";
import * as ReorderArrayElementActions from "../actions/ReorderArrayElement";
import * as SelectConditionalProcess from "../actions/SelectConditionalProcess";
import * as SidePanelActions from "../actions/SidePanelActions";
import * as UpdateParameterActions from "../actions/UpdateParameter";
import * as UpdatePromoteResultAs from "../actions/UpdatePromoteResultAction";
import * as UpdateRetryCount from "../actions/UpdateRetryCount";
import * as UpdateSelectedReturnTypeActions from "../actions/UpdateSelectedReturnType";
import * as UpdateSelectedTypeActions from "../actions/UpdateSelectedType";

import update from "immutability-helper";
import { arrayMove } from "react-sortable-hoc";
import * as InitialFetchActions from "../actions/InitialFetchActions";
import * as PasteAction from "../actions/PasteElements";
import * as UpdateElementDescription from "../actions/UpdateElementDescription";
import { migrateElementData } from "../model/Migrations";

export function reduceElements(state: IElementDictionary = {}, action: IAction): IElementDictionary {
    switch (action.type) {
        case PasteAction.PASTE_ELEMENTS:
            const pasteAction = action as PasteAction.PasteElementAction;
            const newStateAfterPaste = Object.assign({}, state) as IElementDictionary;
            for (const pastedElement of pasteAction.pastedElements) {
                newStateAfterPaste[pastedElement.id] = migrateElementData(pastedElement.element);
            }
            return newStateAfterPaste;

        case UpdatePromoteResultAs.UPDATE_PROMOTE_RESULT_AS:
            const promoteAction = action as UpdatePromoteResultAs.UpdatePromoteResultAction;
            return update(state, {
                [promoteAction.elementId as string]: {
                    promoteResultAs: { $set: promoteAction.promoteResultAs }
                }
            });

        case AddOrUpdateElementActions.CHANGE_OMIT_RESULT_LOGGING: {
            const updateLogResultAction = action as AddOrUpdateElementActions.IUpdateLogResultAction;
            return update(state, {
                [updateLogResultAction.elementId as string]: {
                    shouldNotLogResult: { $set: updateLogResultAction.newValue }
                }
            });
        }

        case AddOrUpdateElementActions.CHANGE_SHOULD_DISPOSE: {
            const updateDisposableAction = action as AddOrUpdateElementActions.IUpdateDisposableAction;
            return update(state, {
                [updateDisposableAction.elementId as string]: {
                    shouldDispose: { $set: updateDisposableAction.newValue }
                }
            });
        }

        case AddOrUpdateElementActions.UPDATE_ELEMENT_NAME:
            const updateNameAction = action as AddOrUpdateElementActions.UpdateElementNameAction;
            return update(state, { [updateNameAction.elementId as string]: { name: { $set: updateNameAction.name } } });

        case UpdateElementDescription.UPDATE_ELEMENT_DESCRIPTION:
            const updateDescriptionAction = action as UpdateElementDescription.UpdateElementDescriptionAction;
            return update(state, { [updateDescriptionAction.elementId as string]: { description: { $set: updateDescriptionAction.description } } });

        case InitialFetchActions.SET_INITIAL_ELEMENTS: {
            const initialElements = action as InitialFetchActions.InitialElements;

            // FRP-2083. Required for pre-6.0 processes with data shapes to work correctly in 6.0+.
            for (const key in initialElements.initialElements) {
                const element = initialElements.initialElements[key];

                if (element.id.startsWith(ElementType.DataObjectReference) && element.type === ElementType.Dmn) {
                    initialElements.initialElements[key] = { ...element, type: ElementType.DataObjectReference };
                }

                if (element.id.startsWith(ElementType.DataStoreReference) && element.type === ElementType.DataObjectReference) {
                    initialElements.initialElements[key] = { ...element, type: ElementType.DataStoreReference };
                }
            }

            return initialElements.initialElements;
        }

        case UpdateRetryCount.UPDATE_RETRY_COUNT:
            const updateRetryCount = action as UpdateRetryCount.UpdateRetryCountAction;
            return update(state, {
                [updateRetryCount.elementId]: {
                    maxRetryCount: { $set: updateRetryCount.retries }
                }
            });

        case UpdateParameterActions.TOGGLE_WORD_WRAP: {
            const toggleWordWrapAction = action as UpdateParameterActions.ToggleWordWrapAction;
            if (state[toggleWordWrapAction.elementId as string] != null) {
                const parameterType = "parameters";

                // Get the current disable word wrap state. The key is a dot notatied string.
                const currentParameterObject: ParameterEditorValue = _.get(state[toggleWordWrapAction.elementId as string][parameterType], toggleWordWrapAction.key as string);
                if (currentParameterObject == null) {
                    return state;
                }
                // Invert the current work wrap and create a object
                const newParameterObject: Partial<ParameterEditorValue> = {
                    disableWordWrap: !currentParameterObject.disableWordWrap
                };

                // Use set to create a nested object with the key
                const newExpressionObject = _.set({}, toggleWordWrapAction.key as string, newParameterObject);

                // Get existing parameters to a new object as redux state should not be edited directly
                const existingParametersForElement = Object.assign({}, state[toggleWordWrapAction.elementId as string][parameterType]);

                const newElements: { [key: string]: any } = {
                    [toggleWordWrapAction.elementId as string]: Object.assign({},
                        state[toggleWordWrapAction.elementId as string],
                        { [parameterType]: _.merge({}, existingParametersForElement, newExpressionObject) }) // Deeply merge the new toggle state onto the existing parameter structure
                };
                return Object.assign({}, state, newElements);
            }
            return state;
        }

        case UpdateParameterActions.UPDATE_PARAMETER: {
            const updateParameterAction = action as UpdateParameterActions.UpdateParameterAction;
            const newParameterObject = {};
            if (state[updateParameterAction.elementId as string] != null) {
                const parameterType = "parameters";
                _.set(newParameterObject, updateParameterAction.key as string, updateParameterAction.newValue);
                const newParameters = { [parameterType]: _.merge({}, state[updateParameterAction.elementId as string][parameterType], newParameterObject) };
                const newElement = { [updateParameterAction.elementId as string]: Object.assign({}, state[updateParameterAction.elementId as string], newParameters) };
                return Object.assign({}, state, newElement);
            }
            return state;
        }

        case UpdateParameterActions.CHANGE_FROM_ARRAY_TO_EXPRESSION: {
            const changeToExpressionAction = action as UpdateParameterActions.ArrayTypeChangeAction;
            if (state[changeToExpressionAction.elementId as string] != null) {
                const parameterType = "parameters";
                const emptyExpressionValue: ParameterEditorValue = {
                    mode: "csharp",
                    value: ""
                };
                const newExpressionObject = _.set({}, changeToExpressionAction.key as string, emptyExpressionValue);
                const existingParametersForElement = Object.assign({}, state[changeToExpressionAction.elementId as string][parameterType]);

                // _.merge can not merga an {} into an [] so we have to first delete the array property from the object and then we can merge
                _.unset(existingParametersForElement, changeToExpressionAction.key as string);
                const newElements: { [key: string]: any } = {
                    [changeToExpressionAction.elementId as string]: Object.assign({},
                        state[changeToExpressionAction.elementId as string],
                        { [parameterType]: _.merge({}, existingParametersForElement, newExpressionObject) })
                };
                return Object.assign({}, state, newElements);
            }
            return state;
        }
        case UpdateParameterActions.CHANGE_FROM_EXPRESSION_TO_ARRAY: {
            const changeToArrayAction = action as UpdateParameterActions.ArrayTypeChangeAction;
            if (state[changeToArrayAction.elementId as string] != null) {
                const parameterType = "parameters";
                const newObject = {};
                const emptyArrayValue: any[] = [];
                _.set(newObject, changeToArrayAction.key as string, emptyArrayValue);
                const newParameters = { [parameterType]: _.merge({}, state[changeToArrayAction.elementId as string][parameterType], newObject) };
                const newElements: { [key: string]: any } = { [changeToArrayAction.elementId as string]: Object.assign({}, state[changeToArrayAction.elementId as string], newParameters) };
                return Object.assign({}, state, newElements);
            }
            return state;
        }
        case UpdateParameterActions.SET_INITIAL_PARAMETER: {
            const setInitialAction = action as UpdateParameterActions.SetInitialParametersAction;
            const initialParams = { parameters: setInitialAction.newParameters };
            const newElements = { [setInitialAction.elementId]: Object.assign({}, state[setInitialAction.elementId], initialParams) };
            return Object.assign({}, state, newElements);
        }

        case AddArrayElementActions.ADD_ARRAY_ELEMENT:
            {
                const addArrayElementAction = action as AddArrayElementActions.AddArrayElementAction;
                const parameterType = "parameters";
                const clonedParameters = _.cloneDeep(state[addArrayElementAction.elementId][parameterType]);
                let newArrayObject = _.get(clonedParameters, addArrayElementAction.key);
                // Really should not happen.
                if (!_.isArray(newArrayObject)) {
                    newArrayObject = [];
                    _.set(clonedParameters, addArrayElementAction.key, newArrayObject);
                }
                const objectParams = Object.assign({}, addArrayElementAction.defaultParameters);

                newArrayObject.push(objectParams);

                const newElement = { [addArrayElementAction.elementId]: Object.assign({}, state[addArrayElementAction.elementId], { [parameterType]: clonedParameters }) };
                return Object.assign({}, state, newElement);
            }

        case RemoveArrayElementActions.REMOVE_ARRAY_ELEMENT:
            {
                const removeArrayElementAction = action as RemoveArrayElementActions.RemoveArrayElementAction;
                const parameterType = "parameters";
                const clonedParameters = _.cloneDeep(state[removeArrayElementAction.elementId][parameterType]);
                const existingArray: any[] = _.get(clonedParameters, removeArrayElementAction.key);
                // Filter the array to a new result where the index that come from the action is removed
                const filteredArray = existingArray.filter((x, i) => i !== removeArrayElementAction.index);
                _.set(clonedParameters, removeArrayElementAction.key, filteredArray);

                return update(state, {
                    [removeArrayElementAction.elementId]: {
                        [parameterType]: { $set: clonedParameters }
                    }
                });
            }
        case ReorderArrayElementActions.REORDER_ARRAY_ELEMENT:
            {
                const reorderArrayElementAction = action as ReorderArrayElementActions.ReorderArrayElementAction;
                const parameterType = "parameters";
                const clonedParameters = _.cloneDeep(state[reorderArrayElementAction.elementId][parameterType]);
                const existingArray = _.get(clonedParameters, reorderArrayElementAction.key);

                const reOrderedArray = arrayMove(existingArray, reorderArrayElementAction.oldIndex, reorderArrayElementAction.newIndex);
                _.set(clonedParameters, reorderArrayElementAction.key, reOrderedArray);
                return update(state, {
                    [reorderArrayElementAction.elementId]: {
                        [parameterType]: { $set: clonedParameters }
                    }
                });
            }
        case UpdateSelectedTypeActions.UPDATE_SELECTED_TEST_TASK_TYPE:
        case UpdateSelectedTypeActions.UPDATE_SELECTED_TYPE:
            const updateSelectedTypeAction = action as UpdateSelectedTypeActions.UpdateSelectedTypeAction;

            return update(state, {
                [updateSelectedTypeAction.elementId]: {
                    selectedTypeId: { $set: updateSelectedTypeAction.selectedTypeId },
                    parameters: { $set: updateSelectedTypeAction.defaultParameters }
                }
            });

        case UpdateSelectedReturnTypeActions.UPDATE_SELECTED_RETURN_TYPE:
            const updateSelectedReturnTypeAction = action as UpdateSelectedReturnTypeActions.UpdateSelectedReturnTypeAction;

            return update(state, {
                [updateSelectedReturnTypeAction.elementId]: {
                    selectedTypeId: { $set: updateSelectedReturnTypeAction.selectedReturnType },
                    parameters: { $set: updateSelectedReturnTypeAction.parameters }
                }
            });

        case SelectConditionalProcess.SELECT_CONDITIONAL_PROCESS:
            const selectConditionalProcessAction = action as SelectConditionalProcess.SelectConditionalProcessAction;
            return update(state, {
                [selectConditionalProcessAction.elementId]: {
                    parameters: {
                        processGuid: { $set: selectConditionalProcessAction.selectedProcessGuid },
                        parameters: { $set: selectConditionalProcessAction.defaultParameters }
                    }
                }
            });

        case SidePanelActions.ADD_TEST_PROCESS:
        case AddOrUpdateElementActions.ADD_GLOBAL_ERROR_HANDLER:
            const addElementAction = action as AddOrUpdateElementActions.AddElementAction;
            const addedElement = Object.assign({}, addElementAction.element);
            return Object.assign({}, state, { [addElementAction.element.id]: addedElement });

        case RemoveElementActions.REMOVE_GLOBAL_ERROR_HANDLER:
            const removeElementAction = action as RemoveElementActions.RemoveElementAction;
            return Object.assign({}, _.omit(state, removeElementAction.elementId)) as IElementDictionary;

        case AddOrUpdateElementActions.BATCH_UPDATE_ELEMENTS:
            {
                const batchAction = action as AddOrUpdateElementActions.BatchUpdateAction;
                const newState = Object.assign({}, state) as IElementDictionary;
                for (const addedBatchElement of batchAction.batch.added) {
                    newState[addedBatchElement.id] = addedBatchElement;
                }
                for (const updatedBatchElement of batchAction.batch.updated) {
                    const updatedElem = _.merge({}, state[updatedBatchElement.id], updatedBatchElement) as IElement;
                    newState[updatedElem.id] = updatedElem;
                }
                for (const deletedBatchElement of batchAction.batch.deleted) {
                    delete newState[deletedBatchElement];
                }
                return newState;
            }
        case ImportExportActions.CLEAR_ELEMENT_STATE:
            return {};

        default:
            return state;
    }
}

export const elements = reduceElements;
